import { useSyncExternalStore } from "react";
import { FISHBOWL_LOCATION_FAVORITES_KEY, resetFavoriteLocationsCache } from "./fishbowlLocationFavorites";

// Per-browser inbox preferences (Devon, 2026-09-28: localStorage only, no server table yet).
// One versioned envelope under a single key, a tiny subscriber list so the inbox re-renders
// when /account changes a setting in another tab, and a whitelist hook for signOut, which
// otherwise wipes localStorage and would make the preference look like it never saved.

export const INBOX_PREFS_KEY = "cr_inbox_prefs";

export type TimelineOrder = "oldest" | "newest";
export type DashboardView = "overview" | "list" | "outlook" | "hybrid";

// The six special lists the Actionable view stacks, in management's order. Mirrors
// SPECIAL_LIST_KEYS in the API's helpers/inbox-queue-rules.js.
export const SPECIAL_LIST_KEYS = [
    "intake",
    "needs_payment",
    "payment_issue_restock",
    "pick_dont_ship_due",
    "backorder_fulfillable",
    "follow_up",
] as const;

export interface InboxPrefs {
    version: 1;
    /** Message order inside a request's timeline. Applies to the request page only. */
    timelineOrder: TimelineOrder;
    /** Sublists shown in the Actionable view, in display order (special lists, then category:/status: keys). */
    actionableLists: string[];
    /** Suggested Follow Up threshold in days. */
    followUpDays: number;
    /** Last chosen dashboard tab. */
    dashboardView?: DashboardView;
}

export const DEFAULT_INBOX_PREFS: InboxPrefs = {
    version: 1,
    timelineOrder: "oldest",
    actionableLists: [...SPECIAL_LIST_KEYS],
    followUpDays: 3,
};

const listeners = new Set<() => void>();
let cached: InboxPrefs | null = null;

function normalize(raw: any): InboxPrefs {
    const out: InboxPrefs = { ...DEFAULT_INBOX_PREFS, actionableLists: [...DEFAULT_INBOX_PREFS.actionableLists] };
    if (!raw || typeof raw !== "object") return out;
    if (raw.timelineOrder === "oldest" || raw.timelineOrder === "newest") out.timelineOrder = raw.timelineOrder;
    if (Array.isArray(raw.actionableLists)) {
        out.actionableLists = raw.actionableLists.filter((k: unknown) => typeof k === "string" && k.length > 0);
    }
    const days = Number(raw.followUpDays);
    if (Number.isFinite(days) && days >= 0 && days <= 365) out.followUpDays = days;
    if (["overview", "list", "outlook", "hybrid"].includes(raw.dashboardView)) out.dashboardView = raw.dashboardView;
    return out;
}

export function readInboxPrefs(): InboxPrefs {
    if (cached) return cached;
    try {
        const stored = localStorage.getItem(INBOX_PREFS_KEY);
        cached = normalize(stored ? JSON.parse(stored) : null);
    } catch {
        cached = normalize(null);
    }
    return cached;
}

export function writeInboxPrefs(patch: Partial<Omit<InboxPrefs, "version">>): InboxPrefs {
    const next = normalize({ ...readInboxPrefs(), ...patch });
    cached = next;
    try {
        localStorage.setItem(INBOX_PREFS_KEY, JSON.stringify(next));
    } catch {
        // private mode or blocked storage: keep the in-memory value for this session
    }
    listeners.forEach((l) => l());
    return next;
}

export function subscribeInboxPrefs(listener: () => void): () => void {
    listeners.add(listener);
    const onStorage = (e: StorageEvent) => {
        if (e.key === INBOX_PREFS_KEY || e.key === null) {
            cached = null;
            listener();
        }
    };
    window.addEventListener("storage", onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener("storage", onStorage);
    };
}

/** React hook: [prefs, update]. The snapshot is referentially stable until a write. */
export function useInboxPrefs(): [InboxPrefs, (patch: Partial<Omit<InboxPrefs, "version">>) => void] {
    const prefs = useSyncExternalStore(subscribeInboxPrefs, readInboxPrefs, () => DEFAULT_INBOX_PREFS);
    return [prefs, writeInboxPrefs];
}

// Per-browser settings that survive signOut's localStorage.clear(). Add a key here
// when a new local preference should not vanish on logout.
const PRESERVED_KEYS = [INBOX_PREFS_KEY, FISHBOWL_LOCATION_FAVORITES_KEY];

/**
 * Run a function that clears localStorage (signOut, ghosting) while keeping the inbox
 * preferences and the other preserved keys. Restores them afterwards even if the
 * function throws.
 */
export function preservingInboxPrefs<T>(fn: () => T): T {
    const snapshots: Record<string, string> = {};
    for (const key of PRESERVED_KEYS) {
        try {
            const value = localStorage.getItem(key);
            if (value != null) snapshots[key] = value;
        } catch {
            // unreadable storage: nothing to restore
        }
    }
    try {
        return fn();
    } finally {
        for (const key of Object.keys(snapshots)) {
            try {
                localStorage.setItem(key, snapshots[key]);
            } catch {
                // ignore
            }
        }
        cached = null;
        resetFavoriteLocationsCache();
    }
}
